"use client"

import { Button } from "@/components/buttons/button"
import { Slider } from "@/components/ui/slider"
import { LoadingSpinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { PAYG_FEATURES } from "./constants"
import type { PaygPricingItem } from "./types"

interface PaygSectionProps {
    pricing: PaygPricingItem
    amountCents: number
    onAmountChange: (amountCents: number) => void
    onAmountCommit: (amountCents: number) => void
    autoRenew: boolean
    autoRenewTopUpAmountCents?: number | null
    onAutoRenewChange: (enabled: boolean) => void
    onCheckout: () => void
    isLoading?: boolean
    autoRenewDisabled?: boolean
    autoRenewMessage?: string
}

export function PaygSection({
    pricing,
    amountCents,
    onAmountChange,
    onAmountCommit,
    autoRenew,
    autoRenewTopUpAmountCents,
    onAutoRenewChange,
    onCheckout,
    isLoading,
    autoRenewDisabled,
    autoRenewMessage,
}: PaygSectionProps) {
    const credits = Math.round(amountCents / pricing.rateCents)
    const amount = amountCents / 100
    const rate = pricing.rateCents / 100
    const hasChangedRenewalAmount =
        autoRenewTopUpAmountCents != null &&
        autoRenewTopUpAmountCents !== amountCents
    const configuredRenewalAmount = (autoRenewTopUpAmountCents ?? amountCents) / 100
    const configuredRenewalCredits = Math.round(
        (autoRenewTopUpAmountCents ?? amountCents) / pricing.rateCents,
    )

    return (
        <section className="mx-auto mt-8 w-full max-w-[560px] px-4 sm:px-0">
            <div className="rounded-[18px] border-2 border-[#7a6bff] bg-white px-5 py-7 sm:px-7 sm:py-[30px]">
                <h2 className="mb-[14px] text-xl font-bold text-[#0f1222]">Pay As You Go</h2>
                <p className="mb-[6px] text-[15px] leading-6 text-[#4a4f63]">
                    No subscription. Buy credits when you need them — a simple flat rate of ${rate.toFixed(2)} per credit.
                </p>

                <div className="rounded-[14px] border border-[#e5e2ff] bg-[#f6f5ff] p-5 sm:p-6" aria-live="polite">
                    <div className="mb-2 flex items-baseline justify-between gap-4">
                        <span className="text-sm text-[#8a8fa3]">You pay</span>
                        <span className="text-[40px] font-extrabold tracking-[-1.5px] text-[#0f1222]">${amount.toLocaleString()}</span>
                    </div>
                    <Slider
                        className="my-5 cursor-pointer [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:cursor-pointer [&_[role=slider]]:border-[3px] [&_[role=slider]]:border-white [&_[role=slider]]:bg-[#5b4bff] [&_[role=slider]]:shadow-md [&_[data-orientation=horizontal]]:h-[7px] [&_[data-orientation=horizontal]]:bg-[#ddd9ff] [&_[data-orientation=horizontal]>span]:bg-transparent"
                        min={pricing.minAmountCents}
                        max={pricing.maxAmountCents}
                        step={pricing.stepAmountCents}
                        value={[amountCents]}
                        onValueChange={(value) => onAmountChange(value[0] ?? pricing.minAmountCents)}
                        onValueCommit={(value) => onAmountCommit(value[0] ?? pricing.minAmountCents)}
                        aria-label="PAYG purchase amount"
                    />
                    <div className="flex justify-between text-xs text-[#8a8fa3]">
                        <span>${(pricing.minAmountCents / 100).toLocaleString()}</span>
                        <span>${(pricing.maxAmountCents / 100).toLocaleString()}</span>
                    </div>
                    <div className="mt-4 border-t border-[#e5e2ff] pt-4">
                        <span className="text-2xl font-extrabold text-[#0f1222]">{credits.toLocaleString()} </span>
                        <span className="text-sm text-[#8a8fa3]">credits</span>
                    </div>
                </div>

                <div className="mt-5 flex items-center justify-between gap-4 border-t border-[#e5e2ff] px-0.5 pt-4">
                    <div>
                        <p className="text-sm font-bold text-[#0f1222]">Auto Renewal</p>
                        <p className="mt-0.5 text-xs text-[#8a8fa3]">Automatically top up when your credits run low</p>
                    </div>
                    <Switch
                        checked={autoRenew}
                        onCheckedChange={onAutoRenewChange}
                        disabled={autoRenewDisabled}
                        aria-label="Enable Auto Renewal"
                        aria-busy={isLoading}
                        className="h-7 w-[50px] border-0 p-[3px] data-[state=checked]:bg-[#5b4bff] data-[state=unchecked]:bg-[#cfd2de] [&>span]:h-[22px] [&>span]:w-[22px] data-[state=checked]:[&>span]:translate-x-[22px]"
                    />
                </div>
                {autoRenewMessage && (
                    <p className="mt-2 text-xs text-[#8a8fa3]">{autoRenewMessage}</p>
                )}
                <p className="mt-3 rounded-lg border border-[#e5e2ff] bg-[#f6f5ff] px-3 py-2 text-xs leading-5 text-[#4a4f63]">
                    {!autoRenew ? (
                        <>Turn on Auto Renewal to automatically add {credits.toLocaleString()} credits when your balance reaches 50 credits or fewer. You’ll be charged ${amount.toLocaleString()} using your saved card.</>
                    ) : hasChangedRenewalAmount ? (
                        <>Your current Auto Renewal is set to ${configuredRenewalAmount.toLocaleString()} for {configuredRenewalCredits.toLocaleString()} credits. Complete a new purchase with Auto Renewal on to change it to ${amount.toLocaleString()} for {credits.toLocaleString()} credits.</>
                    ) : (
                        <>When your balance reaches 50 credits or fewer, we’ll automatically charge your saved card ${amount.toLocaleString()} and add {credits.toLocaleString()} credits. You can turn this off at any time.</>
                    )}
                </p>
                <p className="mt-2 text-xs text-[#8a8fa3]">
                    Taxes are calculated by Stripe during checkout. Auto Renewal only starts after you explicitly opt in.
                </p>

                <Button className="mt-[22px] h-auto w-full rounded-[11px] bg-[#0f1222] py-3.5 text-[15px] font-bold text-white hover:bg-[#23263a]" onClick={onCheckout} disabled={isLoading} aria-busy={isLoading}>
                    {isLoading ? <LoadingSpinner /> : "Buy Credits"}
                </Button>

                <ul className="mt-6 space-y-0">
                    {PAYG_FEATURES.map((feature, index) => (
                        <li key={feature.text} className={`flex items-start gap-2.5 py-[7px] text-sm text-[#2b2f43] ${index === 0 ? "font-bold" : ""}`}>
                            <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#5b4bff] text-xs leading-none text-white">✓</span>
                            {feature.text}
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}
